/**
 * Graph Layout Utilities
 *
 * Uses Dagre for hierarchical DAG layout with viewport fitting.
 */

import dagre from '@dagrejs/dagre';
import type { Node, Edge } from '../types/api';

export interface LayoutOptions {
  direction?: 'TB' | 'LR';  // Top-Bottom or Left-Right
  nodeWidth?: number;
  nodeHeight?: number;
  rankSep?: number;         // Spacing between dependency levels
  nodeSep?: number;         // Spacing between sibling nodes
}

export interface LayoutResult {
  nodes: Array<{ id: string; x: number; y: number }>;
  width: number;
  height: number;
}

/**
 * Calculate hierarchical layout for nodes using Dagre
 */
export function calculateLayout(
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): LayoutResult {
  const {
    direction = 'LR',      // Left-to-right for workflow feel
    nodeWidth = 180,
    nodeHeight = 80,
    rankSep = 100,         // Space between dependency levels
    nodeSep = 50,          // Space between sibling nodes
  } = options;

  // Handle empty graph
  if (nodes.length === 0) {
    return { nodes: [], width: 0, height: 0 };
  }

  // Handle single node
  if (nodes.length === 1) {
    return {
      nodes: [{ id: nodes[0].id, x: 50, y: 50 }],
      width: nodeWidth + 100,
      height: nodeHeight + 100,
    };
  }

  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: direction,
    ranksep: rankSep,
    nodesep: nodeSep,
    marginx: 20,
    marginy: 20,
  });
  g.setDefaultEdgeLabel(() => ({}));

  // Add nodes
  nodes.forEach(node => {
    g.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  // Add edges
  edges.forEach(edge => {
    // Only add edge if both nodes exist
    if (g.hasNode(edge.from_node_id) && g.hasNode(edge.to_node_id)) {
      g.setEdge(edge.from_node_id, edge.to_node_id);
    }
  });

  // Run layout
  dagre.layout(g);

  // Extract positions
  const layoutNodes = nodes.map(node => {
    const pos = g.node(node.id);
    return {
      id: node.id,
      // Dagre returns center position, convert to top-left
      x: pos.x - nodeWidth / 2,
      y: pos.y - nodeHeight / 2,
    };
  });

  // Calculate graph dimensions
  const graphInfo = g.graph();

  return {
    nodes: layoutNodes,
    width: graphInfo.width || nodeWidth + 100,
    height: graphInfo.height || nodeHeight + 100,
  };
}

/**
 * Scale and center layout to fit viewport while maximizing space usage
 */
export function fitLayoutToViewport(
  layout: LayoutResult,
  viewportWidth: number,
  viewportHeight: number,
  padding = 50
): Array<{ id: string; x: number; y: number }> {
  // Handle empty layout
  if (layout.nodes.length === 0) {
    return [];
  }

  const availableWidth = viewportWidth - padding * 2;
  const availableHeight = viewportHeight - padding * 2;

  // Don't scale if layout already fits
  if (layout.width <= availableWidth && layout.height <= availableHeight) {
    // Just center without scaling
    const offsetX = padding + (availableWidth - layout.width) / 2;
    const offsetY = padding + (availableHeight - layout.height) / 2;

    return layout.nodes.map(node => ({
      id: node.id,
      x: Math.round(node.x + offsetX),
      y: Math.round(node.y + offsetY),
    }));
  }

  // Calculate scale to fit
  const scaleX = availableWidth / layout.width;
  const scaleY = availableHeight / layout.height;
  const scale = Math.min(scaleX, scaleY);

  // Center the scaled graph
  const scaledWidth = layout.width * scale;
  const scaledHeight = layout.height * scale;
  const offsetX = padding + (availableWidth - scaledWidth) / 2;
  const offsetY = padding + (availableHeight - scaledHeight) / 2;

  return layout.nodes.map(node => ({
    id: node.id,
    x: Math.round(node.x * scale + offsetX),
    y: Math.round(node.y * scale + offsetY),
  }));
}

/**
 * Combined function: calculate layout and fit to viewport
 */
export function autoLayout(
  nodes: Node[],
  edges: Edge[],
  viewportWidth: number,
  viewportHeight: number,
  options: LayoutOptions = {}
): Array<{ id: string; x: number; y: number }> {
  const layout = calculateLayout(nodes, edges, options);
  return fitLayoutToViewport(layout, viewportWidth, viewportHeight);
}
